import { IGatsbyImageData } from "gatsby-plugin-image"
import { RenderRichTextData } from "gatsby-source-contentful/rich-text"
import Rollbar from "rollbar"
import { Config } from "./components/common/sections/custom/innovation-lab-form/innovation-lab.interface"
import PageMetadata from "./components/seo/page-metadata.interface"
import { News } from "./ui/components/news-item/news.interface"

export declare type AppFunctionComponent<P = Record<string, unknown>> = (
  props: {
    children?: React.ReactNode | React.ReactNode[]
    className?: string
  } & P
) => JSX.Element | null

export type Nullable<T> = T | null

export interface QueryResult<T> {
  edges: {
    node: T
  }[]
}

export interface QueryResultFlat<T> {
  nodes: T[]
}

export interface PageQueryResult<
  OtherResults extends Record<string, any> = Record<string, any>
> {
  data: {
    meta: PageMetadata
    content: {
      nodes: ContentfulPage[]
    }
  } & OtherResults
}

export interface ContentfulInternal<T> {
  id: string
  internal: {
    type: T
  }
}
export interface RenderRichText extends RenderRichTextData<any> {
  references: any[]
}
export interface GatsbyImage {
  gatsbyImageData: IGatsbyImageData
  description?: string
  file: {
    url: string
  }
  title?: string
}

// TODO: Refactor duplicate type to the ContentfulResponsiveImage from graphql-types.ts
export interface ResponsiveImage {
  description?: string
  mobileImage?: GatsbyImage
  tabletImage?: GatsbyImage
  desktopImage?: GatsbyImage
  shadow?: boolean
  contain?: boolean
  linkUrl?: string
  linkText?: string
}

// TODO: Merge ContentfulImage and GatsbyImage as single type (in as graphql-types.ts ContentfulAsset)
export interface ContentfulImage {
  file: {
    url: string
  }
  title: string
  description?: string
}

// TODO: Refactor duplicate type to the ContentfulCareerOffer from graphql-types.ts
export interface JobOffer {
  city?: string
  jobType?: string
  title?: string
  offerContent?: RenderRichText
  slug?: string
}

// TODO: Refactor duplicate type to the ContentfulSectionElement from graphql-types.ts
export interface ContentfulSectionElement extends ResponsiveImage {
  id: string
  title?: string
  text?: RenderRichText
  label?: string
  image?: GatsbyImage
  list?: string[]
  url?: string
  iconImage?: GatsbyImage
  button?: {
    url: string
    text: string
  }
  slug?: string
  responsiveImage?: ResponsiveImage
}

// TODO: Refactor duplicate type to the ContentfulSection from graphql-types.ts
export interface ContentfulSection extends ResponsiveImage {
  title?: string
  id: string
  responsiveImage?: ResponsiveImage & { description?: string }
  text?: RenderRichText
  image?: GatsbyImage
  label?: string
  media?: {
    title?: string
    description?: string
    file?: {
      url?: string
    }
  }[]
  sectionElement?: ContentfulSectionElement[]
  jobOffers?: JobOffer[]
  node_locale?: string
}

// TODO: Refactor duplicate type to the ContentfulHeroSection from graphql-types.ts
export interface ContentfulHeroSection extends ResponsiveImage {
  primaryTitle?: string
  aboveTitle?: string
  underTitle?: string
  text?: RenderRichText
  responsiveImage?: ResponsiveImage
  node_locale?: string
}

// TODO: Refactor to unique QueryResult from generate interface in graphql-types.ts
export interface SingleQueryResult<T> {
  content: T
}

// TODO: Refactor to unique QueryResult from generate interface in graphql-types.ts
export interface MultipleQueryResult<T> {
  content: QueryResult<T>
}

// TODO: Refactor to unique QueryResult from generate interface in graphql-types.ts
export type HeroSectionQueryResult = SingleQueryResult<ContentfulHeroSection>

export type HeroSectionQueryResultTranslation = SingleQueryResult<
  QueryResultFlat<ContentfulHeroSection>
>

// TODO: Refactor to unique QueryResult from generate interface in graphql-types.ts
export type SectionQueryResult = SingleQueryResult<ContentfulSection>
export type SectionQueryResultTranslated = SingleQueryResult<
  QueryResultFlat<ContentfulSection>
>

// TODO: Refactor to unique QueryResult from generate interface in graphql-types.ts
export interface SectionWithDefaultQueryResult {
  content?: ContentfulSection
  defaultContent: ContentfulSection
}

// TODO: Refactor duplicate type to the ContentfulPageBuilder from graphql-types.ts
export interface ContentfulPage {
  path: string
  meta: PageMetadata
  hero?: ContentfulHeroSection
  section?: ContentfulSection[]
}

export type Language = "en-US"

export type ContentfulTranslatedField<T = string> = {
  [key in Language]: T
}

export enum ContentfulButtonType {
  Button = "button",
  UppercasedLink = "uppercased link",
}

// TODO: Refactor duplicate type to the ContentfulButton from graphql-types.ts
export interface ContentfulButton {
  url: ContentfulTranslatedField
  text: ContentfulTranslatedField
  type: ContentfulButtonType
  context: ContentfulTranslatedField
}

export type useStaticQueryType = ReturnType<typeof jest.fn>

declare global {
  interface Window {
    Rollbar: Rollbar
  }
}

// TODO: Refactor duplicate type to the ContentfulProduct from graphql-types.ts
export interface ContentfulProduct {
  sku: {
    code: string
    id: string
  }
  colorName: string
  colorHex: string
  mobileImage?: GatsbyImage[]
  tabletImage?: GatsbyImage[]
  desktopImage?: GatsbyImage[]
}

// TODO: Refactor duplicate type to the ContentfulVariant from graphql-types.ts
export interface ContentfulVariant {
  colorName?: string
  colorHex?: string
  size?: string
  sku: string
  name: string
  mobileImage?: GatsbyImage[]
  tabletImage?: GatsbyImage[]
  desktopImage?: GatsbyImage[]
  children?: any[]
}

// TODO: Refactor duplicate type to the ContentfulECommerceProduct from graphql-types.ts
export interface ECommerceProduct {
  path: string
  id: string
  productName: string
  priceLabel?: string
  productLabel?: string
  informationLabel?: string
  annotationLabel?: string
  variants: ContentfulVariant[]
}

// Site Builder

export enum HeroBuilderDesktopLayout {
  SeparatedTexts = "separated texts",
  CenteredTexts = "centered texts",
  SingleColumn = "single column",
  LandingPageWithFullWith = "landing page with full width",
}

export enum HeroBuilderMobileLayout {
  HeadingImageText = "heading image text",
  SingleColumn = "single column",
  Fit = "fit",
}

export enum HeroBuilderImageBehavior {
  Fit = "fit",
  Fill = "fill",
  Full = "full",
  Cover = "cover",
  Contain = "contain",
}

// TODO: Refactor duplicate type to the ContentfulHeroBuilder from graphql-types.ts
export interface HeroBuilder {
  id?: string
  internal?: {
    type: HeroBuilderType.Hero
  }
  headingText?: RenderRichText
  text?: RenderRichText
  image?: ResponsiveImage
  layoutDesktop: HeroBuilderDesktopLayout
  layoutMobile: HeroBuilderMobileLayout
  imageBehavior: HeroBuilderImageBehavior
  style?: {
    text_max_width: number
    heading_max_width: number
    min_hero_height: number
    landing?: {
      width?: string
      backgroundColor?: string
      linkColor?: string
      buttonColor?: string
      mobileTextAlign?: string
      headerFontWeight?: string
      headerMainTextFontSize?: string
      headerTextFontSize?: string
      mobileMargin?: string
      mobileMarginTop?: string
      mobileHeroHeaderPaddingBottom?: string
      mobileHeroParagraphLineHeight?: string
    }
  }
}

// TODO: Refactor duplicate type to the ContentfulQuote from graphql-types.ts
export interface Quote {
  author?: string
  text: RenderRichText
  style?: {
    text_max_width: number
  }
}

// TODO: Refactor duplicate type to the ContentfulDownloadButton from graphql-types.ts
export interface DownloadButton {
  windowsUrl: string
  macUrl: string
  linuxUrl?: string
  googlePlayUrl?: string
  appStoreUrl?: string
  contentText?: RenderRichText
}

export interface Video {
  title: string
  videoDescription?: {
    videoDescription: string
  }
  url?: string
  media?: {
    file: {
      url: string
    }
  }
  autoplay?: boolean
  loop?: boolean
  controlsPanel?: boolean
}

export enum BuilderSectionType {
  Text = "ContentfulText",
  TextWithEmbed = "ContentfulTextWithEmbed",
  List = "ContentfulList",
  NewsletterSection = "ContentfulNewsletterSection",
  Slider = "ContentfulSlider",
  Custom = "ContentfulCustomComponent",
}

export enum HeroBuilderType {
  Hero = "ContentfulHeroBuilder",
  Slider = "ContentfulSlider",
}

// TODO: Refactor duplicate type to the ContentfulSlider from graphql-types.ts
export interface SliderBuilder {
  id: string
  internal: {
    type: BuilderSectionType.Slider | EmbedType.Slider | HeroBuilderType.Slider
  }
  sliderLayout: SliderLayout
  embeds: Embed[]
}

export interface Audio {
  tracks: {
    title: string
    url: string
  }[]
}

// TODO: Refactor duplicate type to the ContentfulQuote from graphql-types.ts
export enum EmbedType {
  ResponsiveImage = "ContentfulResponsiveImage",
  Quote = "ContentfulQuote",
  DownloadButton = "ContentfulDownloadButton",
  ListItem = "ContentfulListItem",
  Video = "ContentfulVideo",
  Slider = "ContentfulSlider",
  Hero = "ContentfulHeroBuilder",
  CustomComponent = "ContentfulCustomComponent",
}

// TODO: Refactor duplicate type to the ContentfulTextWithEmbed from graphql-types.ts
export type Embed =
  | (ContentfulInternal<EmbedType.ResponsiveImage> & ResponsiveImage)
  | (ContentfulInternal<EmbedType.Quote> & Quote)
  | (ContentfulInternal<EmbedType.DownloadButton> & DownloadButton)
  | (ContentfulInternal<EmbedType.ListItem> & ListItemBuilder)
  | (ContentfulInternal<EmbedType.Video> & Video)
  | (ContentfulInternal<EmbedType.Slider> & SliderBuilder)
  | (ContentfulInternal<EmbedType.Hero> & HeroBuilder)
  | (ContentfulInternal<EmbedType.CustomComponent> & CustomComponentType)

export type BuilderSection =
  | TextBuilder
  | TextWithEmbedBuilder
  | ListBuilder
  | NewsletterBuilder
  | SliderBuilder
  | CustomComponentBuilder // TODO: add others sections

export type BuilderHero = HeroBuilder | SliderBuilder

export enum TextWithEmbedLayout {
  RightText = "right text",
  LeftText = "left text",
  Wide = "wide",
}

export enum TextLayout {
  LeftAligned = "left aligned",
  Centered = "centered",
  TwoTexts = "two texts",
  CenteredTwoTexts = "centered two texts",
  TextsWithRightBackground = "texts with right background",
  TextsWithLeftBackground = "texts with left background",
}

// TODO: Refactor duplicate type to the ContentfulText from graphql-types.ts
export interface TextBuilder {
  id: string
  internal: {
    type: BuilderSectionType.Text
  }
  layout: TextLayout
  title?: string
  text?: RenderRichText
  secondText?: RenderRichText
  style: {
    text_max_width: number
    background?: string
  }
  node_locale?: string
}

// TODO: Refactor duplicate type to the ContentfulTextWithEmbed from graphql-types.ts
export interface TextWithEmbedBuilder {
  id: string
  internal: {
    type: BuilderSectionType.TextWithEmbed
  }
  layout: TextWithEmbedLayout
  title?: string
  text: RenderRichText
  embed?: Embed
  style?: {
    text_max_width?: number
    image_max_width?: number
    background?: string
  }
}

export enum ListItemType {
  Item = "ContentfulListItem",
  AccordionItem = "ContentfulAccordionItem",
  NewsItem = "ContentfulNewsItem",
  CareerOffer = "ContentfulCareerOffer",
}

// TODO: Refactor duplicate type to the ContentfulListItem from graphql-types.ts
export interface ListItemBuilder {
  id: string
  text?: RenderRichText
  title?: string
  label?: string
  image?: GatsbyImage | ContentfulImage
  style?: {
    text_max_width?: number
  }
}

export interface AccordionListItemBuilder {
  id: string
  title: string
  text?: RenderRichText
}

export enum ListSize {
  Small = "small",
  Standard = "standard",
  Medium = "medium",
  Full = "full",
}

export enum ListLayout {
  OneColumn = "one column",
  TwoColumns = "two columns",
  ThreeColumns = "three columns",
  FourColumns = "four columns",
  SixColumns = "six columns",
  TwoColumnsLastWide = "two columns last wide",
  OneColumnWithLeftText = "one column with left text",
  OneColumnNoMargin = "one column no margin",
  Slider = "slider",
  EcommerceSingleColumn = "ecommerce single column",
  Accordion = "accordion",
  Careers = "careers",
}

export enum ListItemLayout {
  VerticalCentered = "vertical centered",
  VerticalLeftAligned = "vertical left aligned",
  Horizontal = "horizontal",
  CenteredHorizontalForOneColumn = "centered horizontal for one column",
  Wide = "wide",
  SingleRow = "single row",
  SingleRowAlternated = "single row alternated",
  Accordion = "accordion",
  Team = "team",
  Overview = "overview",
  ProductItem = "product items",
}

export enum SliderLayout {
  QuoteSlider = "quote slider",
  RegularImageSlider = "regular image slider",
  ProductImageSlider = "product image slider",
  MultiHero = "multi-hero",
}

// TODO: Refactor duplicate type to the ContentfulList from graphql-types.ts
export type ListItem = ContentfulInternal<ListItemType.Item> & ListItemBuilder
export type AccordionItem = ContentfulInternal<ListItemType.AccordionItem> &
  AccordionListItemBuilder
export type NewsItem = ContentfulInternal<ListItemType.NewsItem> & News
export type CareerOffer = ContentfulInternal<ListItemType.CareerOffer> &
  JobOffer

export interface ListBuilder {
  id: string
  internal: {
    type: BuilderSectionType.List
  }
  listLayout: ListLayout
  listSize: ListSize
  listItemLayout: ListItemLayout
  text?: RenderRichText
  items: ListItem[] | AccordionItem[] | NewsItem[]
  style?: {
    item_image_width?: number
    item_background?: string
    text_max_width?: number
    rounded_images?: boolean
    background?: string
    text_as_image_description?: boolean
    margin_top?: number
    no_space_below?: boolean
  }
}

export enum NewsletterLayout {
  Standard = "Standard",
  MiddleContent = "Middle content",
  Kickstarter = "Kickstarter",
  KickstarterInline = "KickstarterInline",
}

// TODO: Refactor duplicate type to the ContentfulNewsletterSection from graphql-types.ts
export interface NewsletterBuilder {
  id: string
  internal: {
    type: BuilderSectionType.NewsletterSection
  }
  title: string
  text: RenderRichText
  disclaimer: {
    disclaimer: string
  }
  mailchimpListId: string
  buttonText: string
  layout: NewsletterLayout
  channel?: string
  style: {
    linkColor?: string
    linkColorHover?: string
  }
}

export interface CountryNode {
  id: string
  label: string
  value: string
  marketCode: number
  type?: string
  flag?: string
  region: string
}

export interface MarketNode {
  name: string
  shortName: string
  code: string
  currency: string
  id: string
}

export interface ProductPriceNode {
  sku: string
  amount: string
  compareAmount: string
  currencyCode: string
  formattedAmount: string
  formattedCompareAmount: string
  b2b: boolean
}

interface MediaContact {
  personName?: string
  email?: string
}
export interface PressRelease {
  id: string
  contentful_id: string
  title: string
  slug: string
  content: RenderRichText
  additionalInfo: RenderRichText
  linkToAssets: string
  linkToPdf: string
  featuredImage: GatsbyImage
  mediaContacts: MediaContact[]
  originalPublishDate: string
}

// Custom components in CF

export enum CustomComponentType {
  InnovationLabForm = "innovation lab form",
  MuditaMomentModule = "mudita moment module",
  BlogFeaturedStories = "blog featured stories",
  Contact = "contact",
  SarCheckerHero = "sar checker hero",
  SarCheckerChart = "sar checker chart",
  SarCheckerSlider = "sar checker slider",
  Stores = "mudita stores",
  FullWidthImage = "full width image",
}

interface CustomConfigInputField {
  sublabel: string
}
interface CustomConfigField {
  label: string
  sublabel: string
  inputs?: CustomConfigInputField[]
  maxChar: number
}

export interface InnovationLabConfig {
  title: string
  subtitle: string
  downloadLinkText: string
  downloadLinkUrl: string
  questionFields: ReadonlyArray<CustomConfigField>
  contactTitle: string
  firstAndLastName: string
  email: string
  phoneNumber: string
  buttonText: string
}

interface CustomComponentImages {
  gatsbyImageData: IGatsbyImageData[]
  title: string
}

export interface CustomComponentBuilder {
  id: string
  internal: {
    type: BuilderSectionType.Custom
  }
  config?: Config
  custom: CustomComponentType
  images?: CustomComponentImages[]
  node_locale?: string
}

export interface CustomFullWidthImage {
  images: { gatsbyImageData: IGatsbyImageData }[]
}
